import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ImageComparison = ({ images, selectedImage }) => {
  const [comparisonMode, setComparisonMode] = useState('side-by-side'); // side-by-side, overlay, slider
  const [leftImage, setLeftImage] = useState(images?.[3]); // Before whitening
  const [rightImage, setRightImage] = useState(images?.[5]); // After whitening
  const [overlayOpacity, setOverlayOpacity] = useState(50);
  const [sliderPosition, setSliderPosition] = useState(50);

  const imageOptions = images?.map(img => ({
    value: img?.id,
    label: `${img?.category} - ${img?.toothNumber} (${img?.date})`
  }));

  const handleLeftImageChange = (imageId) => {
    const image = images?.find(img => img?.id === imageId);
    setLeftImage(image);
  };

  const handleRightImageChange = (imageId) => {
    const image = images?.find(img => img?.id === imageId);
    setRightImage(image);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Comparison Controls */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">
              Image Comparison
            </h3>
            <p className="text-xs text-muted-foreground">
              Compare images to track treatment progress
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant={comparisonMode === 'side-by-side' ? 'default' : 'outline'}
              size="xs"
              iconName="Columns"
              onClick={() => setComparisonMode('side-by-side')}
            >
              Side by Side
            </Button>
            <Button
              variant={comparisonMode === 'overlay' ? 'default' : 'outline'}
              size="xs"
              iconName="Layers"
              onClick={() => setComparisonMode('overlay')}
            >
              Overlay
            </Button>
            <Button
              variant={comparisonMode === 'slider' ? 'default' : 'outline'}
              size="xs"
              iconName="SlidersHorizontal"
              onClick={() => setComparisonMode('slider')}
            >
              Slider
            </Button>
          </div>
        </div>

        {/* Image Selection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <Select
            label="Before / Left Image"
            value={leftImage?.id}
            onChange={handleLeftImageChange}
            options={imageOptions}
            searchable
          />
          <Select
            label="After / Right Image"
            value={rightImage?.id}
            onChange={handleRightImageChange}
            options={imageOptions}
            searchable
          />
        </div>
      </div>
      {/* Comparison Display */}
      <div className="flex-1 relative bg-gray-900 overflow-hidden">
        {comparisonMode === 'side-by-side' && (
          <div className="grid grid-cols-2 h-full">
            {/* Left Image */}
            <div className="relative border-r-2 border-white">
              <img
                src={leftImage?.thumbnail}
                alt={leftImage?.thumbnailAlt}
                className="w-full h-full object-contain"
              />
              <div className="absolute top-4 left-4 bg-blue-600 text-white text-xs px-3 py-1 rounded font-medium">
                BEFORE
              </div>
              <div className="absolute bottom-4 left-4 bg-black/70 text-white text-xs px-3 py-2 rounded">
                <div>{leftImage?.category}</div>
                <div className="text-muted-foreground">{leftImage?.date}</div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <img
                src={rightImage?.thumbnail}
                alt={rightImage?.thumbnailAlt}
                className="w-full h-full object-contain"
              />
              <div className="absolute top-4 right-4 bg-green-600 text-white text-xs px-3 py-1 rounded font-medium">
                AFTER
              </div>
              <div className="absolute bottom-4 right-4 bg-black/70 text-white text-xs px-3 py-2 rounded">
                <div>{rightImage?.category}</div>
                <div className="text-muted-foreground">{rightImage?.date}</div>
              </div>
            </div>
          </div>
        )}

        {comparisonMode === 'overlay' && (
          <div className="relative h-full flex items-center justify-center">
            {/* Base Image */}
            <img
              src={leftImage?.thumbnail}
              alt={leftImage?.thumbnailAlt}
              className="absolute inset-0 w-full h-full object-contain"
            />
            
            {/* Overlay Image */}
            <img
              src={rightImage?.thumbnail}
              alt={rightImage?.thumbnailAlt}
              className="absolute inset-0 w-full h-full object-contain"
              style={{ opacity: overlayOpacity / 100 }}
            />

            {/* Opacity Control */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-3 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium">BEFORE</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={overlayOpacity}
                  onChange={(e) => setOverlayOpacity(Number(e?.target?.value))}
                  className="w-32"
                />
                <span className="text-xs font-medium">AFTER</span>
              </div>
              <div className="text-center text-xs text-muted-foreground mt-1">
                Opacity: {overlayOpacity}%
              </div>
            </div>

            {/* Labels */}
            <div className="absolute top-4 left-4 bg-blue-600 text-white text-xs px-3 py-1 rounded font-medium">
              BEFORE (Base)
            </div>
            <div className="absolute top-4 right-4 bg-green-600 text-white text-xs px-3 py-1 rounded font-medium">
              AFTER (Overlay)
            </div>
          </div>
        )}

        {comparisonMode === 'slider' && (
          <div className="relative h-full flex items-center justify-center overflow-hidden">
            {/* Before Image (Full) */}
            <img
              src={leftImage?.thumbnail}
              alt={leftImage?.thumbnailAlt}
              className="absolute inset-0 w-full h-full object-contain"
            />
            
            {/* After Image (Clipped) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <img
                src={rightImage?.thumbnail}
                alt={rightImage?.thumbnailAlt}
                className="w-full h-full object-contain"
              />
            </div>

            {/* Slider Line */}
            <div
              className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10"
              style={{ left: `${sliderPosition}%` }}
            >
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg">
                <Icon name="ChevronsLeftRight" size={16} className="text-gray-900" />
              </div>
            </div>

            {/* Slider Control */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-3 rounded-lg">
              <input
                type="range"
                min="0"
                max="100"
                value={sliderPosition}
                onChange={(e) => setSliderPosition(Number(e?.target?.value))}
                className="w-48"
              />
            </div>

            {/* Labels */}
            <div className="absolute top-4 left-4 bg-blue-600 text-white text-xs px-3 py-1 rounded font-medium">
              BEFORE
            </div>
            <div className="absolute top-4 right-4 bg-green-600 text-white text-xs px-3 py-1 rounded font-medium">
              AFTER
            </div>
          </div>
        )}
      </div>
      {/* Comparison Stats */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Time Difference</div>
            <div className="text-sm font-semibold text-foreground">
              {Math.abs(
                new Date(rightImage?.date) - new Date(leftImage?.date)
              ) / (1000 * 60 * 60 * 24)} days
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Treatment Type</div>
            <div className="text-sm font-semibold text-foreground">Whitening</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Provider</div>
            <div className="text-sm font-semibold text-foreground">{rightImage?.provider}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Status</div>
            <div className="inline-flex items-center gap-1 text-xs font-medium text-success bg-success/10 px-2 py-1 rounded">
              <Icon name="CheckCircle" size={12} />
              Completed
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageComparison;